import React, { useState } from 'react';
import { ArrowRight, Clock, Loader2, Save, X } from 'lucide-react';
import type { SifaGbu } from '../../../services/sifa/gbuTypes';
import { formatDauer, parseDauer } from '../../../services/sifa/taetigkeitenLogic';
import { KEINE_STUNDENPOSITION_TEXT, pruefeZeitEingabe, viertelstunden, zeitBeschreibungVorgabe } from './gbuModulLogic';

export interface ZeitEingabe {
  datum: string;
  dauerMinuten: number;
  beschreibung: string;
  durchgefuehrtVon: string;
}

interface Props {
  gbu: SifaGbu;
  nutzerName: string | null;
  heute: string;
  /** Es gibt eine passende LV-Position und das Modul darf ins Projekt schreiben. */
  kannAbrechnen: boolean;
  /** Im LV gibt es KEINE buchbare Stundenposition — dann deutlich sagen, wohin die Zeit geht (nie still). */
  keineStundenposition: boolean;
  laeuft: boolean;
  onClose: () => void;
  onWeiter: (e: ZeitEingabe) => void;
}

const FELD = 'w-full px-3 py-2 min-h-[44px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-indigo-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/**
 * „Zeit buchen" an einer GBU (Plan §10): Datum, Dauer, Text. Daraus entsteht
 * IMMER eine Zeile im Taetigkeitsnachweis; abgerechnet wird erst im
 * folgenden Dialog (BillingSelectionModal) und nur, wenn dort eine Position
 * gewaehlt ist — „ohne Leistungsbuchung" bleibt moeglich.
 */
export const GbuZeitBuchen: React.FC<Props> = ({ gbu, nutzerName, heute, kannAbrechnen, keineStundenposition, laeuft, onClose, onWeiter }) => {
  const [datum, setDatum] = useState(heute);
  const [dauer, setDauer] = useState('');
  const [beschreibung, setBeschreibung] = useState(() => zeitBeschreibungVorgabe(gbu));
  const [durchgefuehrtVon, setDurchgefuehrtVon] = useState(nutzerName || '');
  const [fehler, setFehler] = useState<string[]>([]);
  const minuten = parseDauer(dauer);

  const weiter = () => {
    const probleme = pruefeZeitEingabe({ datum, minuten, beschreibung }, heute);
    if (probleme.length > 0 || minuten === null) { setFehler(probleme); return; }
    setFehler([]);
    onWeiter({ datum, dauerMinuten: minuten, beschreibung: beschreibung.trim(), durchgefuehrtVon: durchgefuehrtVon.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={laeuft ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Clock className="w-4 h-4 text-indigo-600" /> Zeit buchen</h3>
            <p className="text-xs text-slate-500 truncate">GBU Nr. {gbu.nummer} — {gbu.titel}</p>
          </div>
          <button onClick={onClose} disabled={laeuft} className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={LABEL}>Datum</span>
              <input type="date" value={datum} max={heute} onChange={(e) => setDatum(e.target.value)} disabled={laeuft} className={FELD} />
            </label>
            <label className="block">
              <span className={LABEL}>Dauer</span>
              <input value={dauer} onChange={(e) => setDauer(e.target.value)} disabled={laeuft} className={FELD} placeholder="1:30 oder 1,5 h" inputMode="decimal" autoFocus />
              <span className="block text-[11px] text-slate-500 mt-1">
                {minuten ? `${formatDauer(minuten)} · abgerechnet ${viertelstunden(minuten).toLocaleString('de-DE')} Std.` : 'Minuten, 1:30 oder 1,5 h'}
              </span>
            </label>
          </div>
          <label className="block">
            <span className={LABEL}>Was wurde getan</span>
            <textarea value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} disabled={laeuft} rows={3} className={`${FELD} min-h-[72px]`} />
          </label>
          <label className="block">
            <span className={LABEL}>Durchgeführt von</span>
            <input value={durchgefuehrtVon} onChange={(e) => setDurchgefuehrtVon(e.target.value)} disabled={laeuft} className={FELD} />
          </label>
          {keineStundenposition ? (
            <p className="p-3 text-xs font-bold text-amber-900 bg-amber-50 border border-amber-300 rounded-xl">{KEINE_STUNDENPOSITION_TEXT}</p>
          ) : (
            <p className="text-[11px] text-slate-500">
              Die Zeit steht danach im Tätigkeitsnachweis (DGUV Vorschrift 2).
              {kannAbrechnen ? ' Im nächsten Schritt wählen Sie die LV-Position für Leistung & Budget — oder bewusst „ohne Leistungsbuchung".' : ' Abgerechnet wird hier nicht (Nur-Ansicht).'}
            </p>
          )}
          {fehler.length > 0 && <ul className="text-xs text-red-700 space-y-0.5">{fehler.map((f) => <li key={f}>{f}</li>)}</ul>}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={laeuft} className="min-h-[44px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">Abbrechen</button>
          <button onClick={weiter} disabled={laeuft} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl disabled:opacity-40">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : kannAbrechnen ? <ArrowRight className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {kannAbrechnen ? 'Weiter zur Abrechnung' : keineStundenposition ? 'Nur im Tätigkeitsnachweis erfassen' : 'Erfassen'}
          </button>
        </div>
      </div>
    </div>
  );
};
